import { useCallback, useMemo } from "react";
import { useMutation, useMutationState, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getHiddenProducts, hideProduct, hiddenProductsQueryKey,
  restoreHiddenProduct, restoreAllHiddenProducts, type HiddenProduct,
} from "../services/HiddenProductsService";

type Change =
  | { kind: "hide"; product: HiddenProduct }
  | { kind: "restore"; product: HiddenProduct }
  | { kind: "restoreAll" };

type ChangeRequest = { change: Change; retailer: string; userId?: string };

function applyChange(products: HiddenProduct[], change: Change): HiddenProduct[] {
  if (change.kind === "restoreAll") return [];
  const remaining = products.filter((item) => item.productId !== change.product.productId);
  return change.kind === "hide" ? [change.product, ...remaining] : remaining;
}

export function useHiddenProducts(retailer: string, userId?: string) {
  const queryClient = useQueryClient();
  const queryKey = hiddenProductsQueryKey(retailer, userId);
  const mutationKey = [...queryKey, "change"];
  const query = useQuery({
    queryKey,
    queryFn: () => getHiddenProducts(retailer),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
  const mutation = useMutation({
    mutationKey,
    // Preserve click order, including Restore all followed by a new hide.
    scope: { id: JSON.stringify(queryKey) },
    mutationFn: async ({ change, retailer, userId }: ChangeRequest) => {
      if (!userId) throw new Error("Sign in to hide products");
      if (change.kind === "hide") return hideProduct(change.product);
      if (change.kind === "restore") await restoreHiddenProduct(retailer, change.product.productId);
      else await restoreAllHiddenProducts(retailer);
    },
    onMutate: async ({ retailer, userId }: ChangeRequest) => {
      const queryKey = hiddenProductsQueryKey(retailer, userId);
      await queryClient.cancelQueries({ queryKey, exact: true });
      return { queryKey, mutationKey: [...queryKey, "change"], retailer, userId };
    },
    onSuccess: async (saved, { change }, context) => {
      if (!context) return;
      // A refresh may have started after the click. Cancel it again before
      // publishing the confirmed change so an older response cannot undo it.
      await queryClient.cancelQueries({ queryKey: context.queryKey, exact: true });
      queryClient.setQueryData<HiddenProduct[]>(context.queryKey, (current = []) =>
        applyChange(current, saved ? { kind: "hide", product: saved } : change)
      );
    },
    onSettled: async (_data, _error, _change, context) => {
      if (!context || queryClient.isMutating({ mutationKey: context.mutationKey, exact: true }) !== 1) return;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: context.queryKey, exact: true }),
        queryClient.invalidateQueries({ queryKey: ["products-browse", context.retailer, context.userId] }),
      ]);
    },
  });
  const pending = useMutationState({
    filters: { mutationKey, exact: true, status: "pending" },
    select: (entry) => (entry.state.variables as ChangeRequest).change,
  });
  // Pending clicks remain visible even if a background read returns an older
  // list. Failed changes drop out naturally, without rolling back other clicks.
  const products = useMemo(
    () => userId ? pending.reduce(applyChange, query.data || []) : [],
    [pending, query.data, userId]
  );
  const change = useCallback(
    (change: Change) => mutation.mutateAsync({ change, retailer, userId }),
    [mutation.mutateAsync, retailer, userId]
  );
  return { ...query, products, change };
}
